import React, { useEffect, useState } from 'react';
import { Card, Table, Badge, Row, Col, Spinner, Alert, Button } from 'react-bootstrap';
import { FaBus, FaArrowLeft, FaTachometerAlt, FaClock, FaHistory } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../styles/Flota.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const FlotaDetalle = () => {
  const { interno } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    cargarDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interno]);

  const cargarDetalle = async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;

      if (!authToken || !user) {
        setError('No hay sesión activa');
        return;
      }

      const userId = user.Legajo || user.CdEmpleado || user.CdUsuario;
      const userRole = user.IdRol || user.tipoUsuario || 1;

      const response = await axios.get(`${API_URL}/api/flota/unidades/${interno}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'x-user-id': String(userId),
          'x-user-role': String(userRole)
        }
      });

      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError('No se pudo cargar el detalle');
      }
    } catch (err) {
      console.error('Error al cargar detalle:', err);
      setError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin registro';
    return new Date(fecha).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatFechaHora = (fecha) => {
    if (!fecha) return 'Sin registro';
    return new Date(fecha).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-AR').format(Math.round(num));
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando detalle de unidad...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flota-detalle">
        <Button 
          variant="outline-secondary" 
          size="sm" 
          onClick={() => navigate('/administrador/flota/unidades')}
          className="mb-3"
        >
          <FaArrowLeft className="me-2" />
          Volver al listado
        </Button>
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  if (!data) return null;

  const { datosPrincipales, historialEstados, actividadReciente, kmsRecientes, estadisticas } = data;

  return (
    <div className="flota-detalle">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2><FaBus className="me-2" />Detalle de Unidad #{datosPrincipales.Interno}</h2>
          <p className="text-muted mb-0">Información completa y seguimiento operativo</p>
        </div>
        <Button 
          variant="outline-secondary" 
          onClick={() => navigate('/administrador/flota/unidades')}
        >
          <FaArrowLeft className="me-2" />
          Volver
        </Button>
      </div>

      {/* Ficha principal */}
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <strong>Datos Principales</strong>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Interno:</strong> {datosPrincipales.Interno}</p>
                  <p><strong>Dominio:</strong> {datosPrincipales.Dominio || 'Sin registro'}</p>
                  <p>
                    <strong>Estado:</strong>{' '}
                    <Badge 
                      bg="secondary"
                      style={{ backgroundColor: datosPrincipales.ColorEstado || '#6c757d' }}
                    >
                      {datosPrincipales.NmEstado}
                    </Badge>
                  </p>
                  <p>
                    <strong>Novedad:</strong>{' '}
                    {datosPrincipales.CdNovedad !== 0 ? (
                      <Badge bg="danger">{datosPrincipales.NmNovedad}</Badge>
                    ) : (
                      <Badge bg="success">Sin novedad</Badge>
                    )}
                  </p>
                </Col>
                <Col md={6}>
                  <p><strong>Base:</strong> {datosPrincipales.NmBase || 'Sin base'}</p>
                  <p><strong>Fecha Ingreso:</strong> {formatFecha(datosPrincipales.FechaIngreso)}</p>
                  <p><strong>Última Actualización:</strong> {formatFechaHora(datosPrincipales.FecUltAct)}</p>
                </Col>
              </Row>
              {datosPrincipales.Obs && (
                <Row className="mt-3">
                  <Col>
                    <Alert variant="info" className="mb-0">
                      <strong>Observaciones:</strong> {datosPrincipales.Obs}
                    </Alert>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex align-items-center mb-2">
                <FaTachometerAlt className="text-primary me-2" size={24} />
                <h6 className="mb-0">Estadísticas (30 días)</h6>
              </div>
              <hr />
              <p className="mb-2">
                <strong>Días Activos:</strong>{' '}
                <Badge bg="primary">{estadisticas.TotalDiasActivos}</Badge>
              </p>
              <p className="mb-2">
                <strong>Total Km:</strong>{' '}
                <span className="text-primary">{formatNumber(estadisticas.TotalKms)}</span>
              </p>
              <p className="mb-0">
                <strong>Promedio Km/día:</strong>{' '}
                <span className="text-info">{formatNumber(estadisticas.PromedioKmsPorDia)}</span>
              </p>
            </Card.Body>
          </Card>

          {datosPrincipales.Litros && (
            <Card>
              <Card.Body>
                <h6 className="mb-3">Datos Técnicos</h6>
                <p className="mb-2"><strong>Litros:</strong> {datosPrincipales.Litros}</p>
                <p className="mb-2"><strong>Caja:</strong> {datosPrincipales.Caja}</p>
                <p className="mb-0"><strong>Diferencial:</strong> {datosPrincipales.Diferencial}</p>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Kilómetros recientes */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <FaTachometerAlt className="me-2" />
              <strong>Kilómetros Recientes (Últimos 30 días)</strong>
            </Card.Header>
            <Card.Body>
              {kmsRecientes.length === 0 ? (
                <Alert variant="warning">No hay registros de kilómetros en los últimos 30 días</Alert>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff' }}>
                      <tr>
                        <th>Fecha</th>
                        <th className="text-end">Kilómetros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kmsRecientes.map((item, idx) => (
                        <tr key={idx}>
                          <td>{formatFecha(item.Fecha)}</td>
                          <td className="text-end">{formatNumber(item.Kms)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Historial de estados */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <FaHistory className="me-2" />
              <strong>Historial de Cambios de Estado</strong>
            </Card.Header>
            <Card.Body>
              {historialEstados.length === 0 ? (
                <Alert variant="info">No hay historial de cambios de estado</Alert>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff' }}>
                      <tr>
                        <th>Fecha/Hora</th>
                        <th>Estado</th>
                        <th>Novedad</th>
                        <th>Base</th>
                        <th>Observaciones</th>
                        <th>Usuario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historialEstados.map((item) => (
                        <tr key={item.ID}>
                          <td>{formatFechaHora(item.FecAct)}</td>
                          <td>
                            <Badge bg="secondary">{item.NmEstado || `Código ${item.CdEstado}`}</Badge>
                          </td>
                          <td>
                            {item.CdNovedad !== 0 ? (
                              <Badge bg="danger">{item.NmNovedad || `Código ${item.CdNovedad}`}</Badge>
                            ) : (
                              <Badge bg="success">Sin novedad</Badge>
                            )}
                          </td>
                          <td>{item.CdBase}</td>
                          <td>{item.Obs || '-'}</td>
                          <td>{item.CdUsuario}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actividad reciente */}
      <Row>
        <Col md={12}>
          <Card>
            <Card.Header>
              <FaClock className="me-2" />
              <strong>Días con Actividad (Últimos 30 días)</strong>
            </Card.Header>
            <Card.Body>
              {actividadReciente.length === 0 ? (
                <Alert variant="warning">No hay registros de actividad en los últimos 30 días</Alert>
              ) : (
                <div className="actividad-grid">
                  {actividadReciente.map((item, idx) => (
                    <Badge 
                      key={idx} 
                      bg="success" 
                      className="me-2 mb-2"
                      style={{ fontSize: '0.85rem' }}
                    >
                      {formatFecha(item.Fecha)}
                    </Badge>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FlotaDetalle;
